/* eslint-disable react/jsx-filename-extension */
import { Loader } from '@transferwise/components';
import { ThemeProvider } from '@wise/components-theming';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { fetchQuotes, fetchRate } from '../../resources/clients';
import { isWiseQuote } from '../../resources/quoteFilters';

import ComparisonTableHero from './ComparisonTableHero';
import styles from './ComparisonTableHeroContainer.module.less';

function extractTwRate(quotes) {
  const twQuotes = quotes.filter((quote) => isWiseQuote(quote));
  return twQuotes.length > 0 ? twQuotes[0].rate : null;
}

class ComparisonTableHeroContainer extends Component {
  constructor(props) {
    super(props);
    const { initialQuotes, midMarketRate } = this.props;
    this.state = {
      loadingQuotes: !initialQuotes.length,
      quotes: initialQuotes,
      midMarketRate,
      loadingMidMarketRate: !midMarketRate,
    };
  }

  async componentDidMount() {
    const { initialQuotes, sourceCurrency, targetCurrency, sendAmount, midMarketRate } = this.props;

    let quotesPromise;
    if (!initialQuotes.length) {
      quotesPromise = this.loadQuotes(sourceCurrency, targetCurrency, sendAmount);
    } else {
      quotesPromise = Promise.resolve(initialQuotes);
    }

    if (!midMarketRate) {
      const midMarketRatePromise = this.loadMidMarketRate(sourceCurrency, targetCurrency);
      const fetchedQuotes = await quotesPromise;
      let fetchedMidMarketRate = extractTwRate(fetchedQuotes);
      if (!fetchedMidMarketRate) {
        try {
          fetchedMidMarketRate = await midMarketRatePromise;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch mid-market rate', error);
          return;
          // Ignore
        }
      }
      this.setState({
        midMarketRate: fetchedMidMarketRate,
        loadingMidMarketRate: false,
      });
    }
  }

  componentDidUpdate(previousProps) {
    const { sourceCurrency, targetCurrency, sendAmount } = this.props;

    if (
      sourceCurrency !== previousProps.sourceCurrency ||
      targetCurrency !== previousProps.targetCurrency ||
      sendAmount !== previousProps.sendAmount
    ) {
      this.loadQuotes(sourceCurrency, targetCurrency, sendAmount);
    }
  }

  async loadQuotes(sourceCurrency, targetCurrency, sendAmount) {
    const quotesResponse = await fetchQuotes(sourceCurrency, targetCurrency, sendAmount);
    const { quotes } = quotesResponse;
    this.setState({
      quotes,
      loadingQuotes: false,
    });
    return quotes;
  }

  loadMidMarketRate(sourceCurrency, targetCurrency) {
    this.setState({ loadingMidMarketRate: true });
    return fetchRate(sourceCurrency, targetCurrency)
      .then((response) => response.route.rate)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch mid-market rate', error);
      });
  }

  render() {
    const {
      sourceCurrency,
      targetCurrency,
      sendAmount,
      excludePartners,
      maxVisibleProviders,
      deliveryEstimationEnabled,
      isTransparencyFooterHidden,
      trackEvent,
    } = this.props;
    const { loadingQuotes, quotes, midMarketRate, loadingMidMarketRate } = this.state;
    return (
      <ThemeProvider theme="personal" screenMode="light" className={styles['theme-without-bg']}>
        {loadingQuotes || loadingMidMarketRate ? (
          <div>
            {' '}
            {/* TODO Might need styling here to Center? */}
            <Loader classNames={styles} data-testid="loader" />
          </div>
        ) : (
          <ComparisonTableHero
            sourceCurrency={sourceCurrency}
            targetCurrency={targetCurrency}
            sendAmount={sendAmount}
            quotes={quotes}
            midMarketRate={midMarketRate}
            excludePartners={excludePartners}
            maxVisibleProviders={maxVisibleProviders}
            deliveryEstimationEnabled={deliveryEstimationEnabled}
            isTransparencyFooterHidden={isTransparencyFooterHidden}
            trackEvent={trackEvent}
          />
        )}
      </ThemeProvider>
    );
  }
}

ComparisonTableHeroContainer.propTypes = {
  sourceCurrency: PropTypes.string.isRequired,
  targetCurrency: PropTypes.string.isRequired,
  sendAmount: PropTypes.number.isRequired,
  midMarketRate: PropTypes.number,

  initialQuotes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      alias: PropTypes.string.isRequired,
      logo: PropTypes.string.isRequired,
      rate: PropTypes.number.isRequired,
      fees: PropTypes.number.isRequired,
      receivedAmount: PropTypes.number.isRequired,
      dateCollected: PropTypes.string.isRequired,
    }),
  ),

  excludePartners: PropTypes.bool,
  maxVisibleProviders: PropTypes.number,
  deliveryEstimationEnabled: PropTypes.bool,
  isTransparencyFooterHidden: PropTypes.bool,
  trackEvent: PropTypes.func,
};

ComparisonTableHeroContainer.defaultProps = {
  initialQuotes: [],
  midMarketRate: undefined,
  maxVisibleProviders: undefined,
  excludePartners: true,
  deliveryEstimationEnabled: false,
  isTransparencyFooterHidden: false,
  trackEvent: () => {},
};

export default ComparisonTableHeroContainer;
